/**
 * iter-205 · DemoPendingOfferBanner.
 *
 * Sticky sticky-style sticky banner shown on /my-plan whenever the
 * user (now a real account) still carries the admin-reserved demo
 * offer in their ``demo_plan_pending`` / ``demo_packs_pending`` /
 * ``demo_addons_pending`` fields. These fields were set during the
 * demo activation, conserved through ``/api/demo/convert`` (iter-202),
 * and are intentionally NOT auto-applied — the user has to opt in.
 *
 * Renders nothing when none of the three pending fields is populated.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ShieldCheck, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { IconToggle } from "../../components/ui/icon-toggle";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { tx } from "../../i18n";

export function DemoPendingOfferBanner({ lang, onActivate }) {
  const { user, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [dismissing, setDismissing] = useState(false);
  const plan = user?.demo_plan_pending;
  const packs = user?.demo_packs_pending || [];
  const addons = user?.demo_addons_pending || [];

  const hasOffer = !!(plan || packs.length || addons.length);
  if (!hasOffer) return null;

  const handleActivate = () => {
    if (onActivate) return onActivate();
    const params = new URLSearchParams();
    if (plan) params.set("plan", plan);
    if (packs.length) params.set("packs", packs.join(","));
    if (addons.length) params.set("addons", addons.join(","));
    params.set("from", "demo_conversion");
    navigate(`/pricing?${params.toString()}`);
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/demo/pending/dismiss");
      if (refreshAuth) await refreshAuth();
    } catch {
      // soft fail — banner stays; user can retry
    } finally {
      setDismissing(false);
    }
  };

  const offerLine = [
    plan && tx(lang, `Plan ${plan}`, `Plan ${plan}`, `Plano ${plan}`),
    packs.length && tx(lang,
      `${packs.length} pack${packs.length > 1 ? "s" : ""}`,
      `${packs.length} pack${packs.length > 1 ? "s" : ""}`,
      `${packs.length} pacote${packs.length > 1 ? "s" : ""}`),
    addons.length && tx(lang,
      `${addons.length} add-on${addons.length > 1 ? "s" : ""}`,
      `${addons.length} extra${addons.length > 1 ? "s" : ""}`,
      `${addons.length} add-on${addons.length > 1 ? "s" : ""}`),
  ].filter(Boolean).join(" · ");

  return (
    <div
      data-testid="demo-pending-offer-banner"
      className="relative overflow-hidden rounded-2xl border border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 p-4 sm:p-5 shadow-sm"
    >
      <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-emerald-200/30 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="h-10 w-10 rounded-full bg-emerald-600 text-white inline-flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-700">
              {tx(lang, "Offer reserved by admin", "Oferta reservada por el admin", "Oferta reservada pelo admin")}
            </p>
            <h3 className="font-display text-base sm:text-lg font-bold text-emerald-900 mt-0.5">
              {tx(lang,
                "Activate the package the admin held for you.",
                "Activa el paquete que el admin reservó para ti.",
                "Ative o pacote que o admin reservou para você.")}
            </h3>
            {offerLine && (
              <p className="text-sm text-emerald-800 mt-1 font-medium" data-testid="demo-pending-offer-line">
                {offerLine}
              </p>
            )}
            <p className="text-xs text-emerald-700/80 mt-1">
              {tx(lang,
                "No charge today — review before you confirm.",
                "Hoy no se cobra — revísalo antes de confirmar.",
                "Hoje sem cobrança — revise antes de confirmar.")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            onClick={handleActivate}
            data-testid="demo-pending-offer-activate"
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1.5 font-semibold"
          >
            <Sparkles className="h-4 w-4" />
            {tx(lang, "Activate offer", "Activar oferta", "Ativar oferta")}
          </Button>
          {handleDismiss && (
            <IconToggle
              onClick={handleDismiss}
              disabled={dismissing}
              data-testid="demo-pending-offer-dismiss"
              aria-label="Dismiss"
              size="lg"
              className="hover:bg-emerald-100 text-emerald-700 border-transparent"
            >
              <X className="h-4 w-4" />
            </IconToggle>
          )}
        </div>
      </div>
    </div>
  );
}

export default DemoPendingOfferBanner;
